import React from "react";
import { Modal, Button, Form } from "react-bootstrap";

import {
  MEDIA_SOURCE_OPTIONS,
  type MediaSource,
} from "@/features/processing/mediaSource";
import type { SongType } from "@/features/processing/processingTypes";
import styles from "@/pages/uploader.module.scss";

const MODAL_MEDIA_SOURCE_INPUT_ID = "kk-upload-confirm-media-source";
const MODAL_GENRE_INPUT_ID = "kk-upload-confirm-genre";
const MODAL_SUBGENRE_INPUT_ID = "kk-upload-confirm-subgenre";

/** Active genre (with its active subgenres) offered by the optional pickers. */
export interface UploadGenreOption {
  genreId: string;
  name: string;
  subgenres: Array<{ subgenreId: string; name: string }>;
}

export interface UploadConfirmModalProps {
  show: boolean;
  fileNames: string[];
  songType: SongType | null;
  mediaSource: MediaSource | "";
  onSongTypeChange: (songType: SongType) => void;
  onMediaSourceChange: (mediaSource: MediaSource | "") => void;
  /** Optional Genre taxonomy pickers (active taxonomy only). */
  genreOptions?: UploadGenreOption[];
  genreId?: string | "";
  subgenreId?: string | "";
  onGenreChange?: (genreId: string | "") => void;
  onSubgenreChange?: (subgenreId: string | "") => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Post-drop gate: Song type (no default) + Source must both be set before
 * Continue starts the upload pool for the parked batch.
 */
export function UploadConfirmModal({
  show,
  fileNames,
  songType,
  mediaSource,
  onSongTypeChange,
  onMediaSourceChange,
  genreOptions,
  genreId = "",
  subgenreId = "",
  onGenreChange,
  onSubgenreChange,
  onCancel,
  onConfirm,
}: UploadConfirmModalProps): React.ReactElement {
  const canContinue = songType != null && Boolean(mediaSource);
  const fileCount = fileNames.length;
  // Genre rules (owner-approved 2026-08-29):
  //   * KK Original (single or batch) — manual optional pickers; batch-wide.
  //   * Commercial single file — optional MANUAL OVERRIDE; blank allows
  //     automatic Commercial Music identification.
  //   * Commercial multiple files — no batch pickers; Genre/Subgenre is
  //     assigned individually per song by identification.
  const commercialBatch = songType === "commercial" && fileCount > 1;
  const commercialSingle = songType === "commercial" && fileCount === 1;
  const showGenrePickers =
    onGenreChange != null && songType != null && !commercialBatch;
  const selectedGenre = (genreOptions ?? []).find((g) => g.genreId === genreId);
  const subgenreChoices = selectedGenre?.subgenres ?? [];

  return (
    <Modal
      show={show}
      onHide={onCancel}
      backdrop="static"
      dialogClassName={styles.confirmDialog}
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm upload</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {fileCount > 0 ? (
          <div
            className={styles.confirmFileSummary}
            title={fileNames.join("\n")}
          >
            <div className={styles.confirmFileLabel}>
              {fileCount === 1
                ? "Artist / Title (from filename)"
                : `Artist / Title · ${fileCount} files`}
            </div>
            {fileCount === 1 ? (
              <div className={styles.confirmFileName}>{fileNames[0]}</div>
            ) : (
              <ul className={styles.confirmFileList}>
                {fileNames.map((name, index) => (
                  <li key={`${index}-${name}`}>{name}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <section className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            Is this a KK Original or Commercial song? *
          </h3>
          <div
            className={styles.songTypeSegmented}
            role="radiogroup"
            aria-label="Song type"
          >
            <button
              type="button"
              role="radio"
              aria-checked={songType === "kk-original"}
              className={`${styles.songTypeOption} ${
                songType === "kk-original" ? styles.songTypeOptionSelected : ""
              }`}
              onClick={() => onSongTypeChange("kk-original")}
              aria-label="Song type KK Original"
            >
              KK Original
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={songType === "commercial"}
              className={`${styles.songTypeOption} ${
                songType === "commercial" ? styles.songTypeOptionSelected : ""
              }`}
              onClick={() => onSongTypeChange("commercial")}
              aria-label="Song type Commercial"
            >
              Commercial
            </button>
          </div>
          {songType == null ? (
            <p className={styles.confirmHint}>Select a song type to continue.</p>
          ) : null}
        </section>

        <section className={styles.formSection}>
          <h3 className={styles.sectionTitle}>Source</h3>
          <Form.Group className={styles.metadataField}>
            <Form.Label htmlFor={MODAL_MEDIA_SOURCE_INPUT_ID}>
              Where did this MP3 come from? *
            </Form.Label>
            <Form.Select
              id={MODAL_MEDIA_SOURCE_INPUT_ID}
              name={MODAL_MEDIA_SOURCE_INPUT_ID}
              size="sm"
              value={mediaSource}
              onChange={(e) =>
                onMediaSourceChange((e.target.value as MediaSource | "") || "")
              }
              aria-required="true"
            >
              <option value="">Select source…</option>
              {MEDIA_SOURCE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </section>

        {commercialBatch ? (
          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Genre</h3>
            <p className={styles.metadataHint}>
              Genre and Subgenre will be assigned <strong>individually per
              song</strong> from Commercial Music identification. You can
              review or change any song&apos;s Genre later in the Song
              Library.
            </p>
          </section>
        ) : null}

        {showGenrePickers ? (
          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Genre</h3>
            {commercialSingle ? (
              <p className={styles.metadataHint}>
                Optional override — leave blank to let Commercial Music
                identification assign Genre/Subgenre automatically. Selecting
                a Genre here makes your choice authoritative for this song.
              </p>
            ) : null}
            <Form.Group className={styles.metadataField}>
              <Form.Label htmlFor={MODAL_GENRE_INPUT_ID}>
                Genre (optional)
              </Form.Label>
              <Form.Select
                id={MODAL_GENRE_INPUT_ID}
                name={MODAL_GENRE_INPUT_ID}
                size="sm"
                value={genreId}
                onChange={(e) => {
                  onGenreChange?.(e.target.value || "");
                  // Subgenre must belong to the chosen genre — always reset.
                  onSubgenreChange?.("");
                }}
              >
                <option value="">No genre</option>
                {(genreOptions ?? []).map((opt) => (
                  <option key={opt.genreId} value={opt.genreId}>
                    {opt.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className={styles.metadataField}>
              <Form.Label htmlFor={MODAL_SUBGENRE_INPUT_ID}>
                Subgenre (optional)
              </Form.Label>
              <Form.Select
                id={MODAL_SUBGENRE_INPUT_ID}
                name={MODAL_SUBGENRE_INPUT_ID}
                size="sm"
                value={subgenreId}
                disabled={!genreId}
                onChange={(e) => onSubgenreChange?.(e.target.value || "")}
              >
                <option value="">No subgenre</option>
                {subgenreChoices.map((opt) => (
                  <option key={opt.subgenreId} value={opt.subgenreId}>
                    {opt.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </section>
        ) : null}

        {songType === "kk-original" ? (
          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Song metadata</h3>
            <p className={styles.metadataHint}>
              Artist is always <strong>KrossKast</strong>. Song title is taken from
              each MP3 filename (for example,{" "}
              <code>KrossKast - 3AM Somewhere.mp3</code> → <em>3AM Somewhere</em>).
              No need to enter Title or Artist.
            </p>
          </section>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-dark"
          className={styles.confirmCancelButton}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm} disabled={!canContinue}>
          Continue upload
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
